/**
 * WebhookLeadsDialog
 * ──────────────────
 * Per-client webhook configuration. Lets the agency admin:
 *   1. Generate (or rotate) the per-client shared-secret token.
 *   2. Copy the full webhook URL to paste into WordPress / WPForms / Zapier.
 *   3. Optionally override the field mapping (e.g. "client_email" → email).
 *
 * Designed as the alternative to Gmail polling, so AEGIS can drop the
 * `gmail.readonly` restricted scope when the client doesn't want OAuth.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Copy,
  RefreshCw,
  AlertTriangle,
  Webhook as WebhookIcon,
  CheckCircle2,
  Trash2,
  ExternalLink,
  Eye,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STD_FIELDS = [
  { key: 'email', label: 'Email', placeholder: 'your-email, client_email' },
  { key: 'phone', label: 'Téléphone', placeholder: 'your-phone, telephone' },
  { key: 'full_name', label: 'Nom complet', placeholder: 'your-name, full_name' },
  { key: 'first_name', label: 'Prénom', placeholder: 'first_name, prenom' },
  { key: 'last_name', label: 'Nom', placeholder: 'last_name, nom' },
  { key: 'company', label: 'Société', placeholder: 'company, societe' },
  { key: 'message', label: 'Message', placeholder: 'your-message, content' },
  { key: 'subject_request', label: 'Sujet', placeholder: 'subject, sujet' },
  { key: 'postal_code', label: 'Code postal', placeholder: 'zip, code_postal' },
  { key: 'city', label: 'Ville', placeholder: 'city, ville' },
];

export default function WebhookLeadsDialog({ open, onOpenChange, client }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldMap, setFieldMap] = useState({});
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients/${client.id}/webhook`, { withCredentials: true });
      setConfig(data);
      // Convert backend [array] → comma string for the editor
      const map = {};
      Object.entries(data.field_map || {}).forEach(([k, v]) => {
        map[k] = Array.isArray(v) ? v.join(', ') : String(v || '');
      });
      setFieldMap(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [client]);

  const fetchRecent = useCallback(async () => {
    if (!client?.id) return;
    setRecentLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients/${client.id}/webhook/recent`, { withCredentials: true });
      setRecent(data.items || []);
    } catch {
      // silent — endpoint may 404 on legacy clients without inspection data
      setRecent([]);
    } finally {
      setRecentLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (open) {
      fetchConfig();
      fetchRecent();
    }
  }, [open, fetchConfig, fetchRecent]);

  const webhookUrl = config?.token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/leads/${client?.id}?token=${config.token}`
    : null;

  const generateOrRotate = async () => {
    if (config?.configured && !window.confirm('Régénérer le token va INVALIDER l\'URL actuelle. Tous les formulaires utilisant l\'ancien token cesseront de fonctionner. Continuer ?')) {
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/clients/${client.id}/webhook/regenerate-token`, {}, { withCredentials: true });
      setConfig((c) => ({ ...(c || {}), token: data.token, configured: true }));
      toast.success(config?.configured ? 'Token régénéré' : 'Webhook activé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const disableWebhook = async () => {
    if (!window.confirm('Désactiver le webhook ? Les leads précédents sont conservés mais aucun nouveau ne sera reçu.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/clients/${client.id}/webhook`, { withCredentials: true });
      setConfig({ ...config, token: null, configured: false, field_map: {} });
      setFieldMap({});
      toast.success('Webhook désactivé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const saveFieldMap = async () => {
    setSaving(true);
    try {
      const payload = { field_map: {} };
      Object.entries(fieldMap).forEach(([k, v]) => {
        const arr = String(v || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (arr.length) payload.field_map[k] = arr;
      });
      await axios.put(`${API}/clients/${client.id}/webhook`, payload, { withCredentials: true });
      toast.success('Mapping enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copié`),
      () => toast.error('Impossible de copier'),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="webhook-leads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WebhookIcon className="w-5 h-5 text-violet-600" />
            Webhook leads — {client?.name}
          </DialogTitle>
          <DialogDescription>
            Recevez les leads directement depuis les formulaires WordPress / WPForms / Zapier sans passer par Gmail. Le webhook est unique par client et sécurisé par token.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-neutral-500">Chargement…</div>
        ) : (
          <div className="space-y-5">
            {/* Token & URL */}
            <div className="border rounded-lg p-4 bg-neutral-50">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-medium text-sm flex items-center gap-2">
                    {config?.configured ? (
                      <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Webhook actif</>
                    ) : (
                      <><AlertTriangle className="w-4 h-4 text-amber-600" /> Non configuré</>
                    )}
                  </div>
                  <p className="text-[12px] text-neutral-500 mt-0.5">
                    {config?.configured
                      ? 'Collez l\'URL ci-dessous dans le plugin webhook de votre formulaire.'
                      : 'Cliquez sur "Générer le webhook" pour activer la réception de leads.'}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={config?.configured ? 'outline' : 'default'}
                  onClick={generateOrRotate}
                  disabled={saving}
                  data-testid="webhook-regen-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${saving ? 'animate-spin' : ''}`} />
                  {config?.configured ? 'Régénérer' : 'Générer le webhook'}
                </Button>
              </div>

              {webhookUrl && (
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-wide text-neutral-500">URL du webhook</Label>
                  <div className="flex items-stretch gap-2">
                    <Input
                      value={webhookUrl}
                      readOnly
                      className="font-mono text-[11px] bg-white"
                      onClick={(e) => e.target.select()}
                      data-testid="webhook-url-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(webhookUrl, 'URL')}
                      data-testid="webhook-copy-url-btn"
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copier
                    </Button>
                  </div>
                  <p className="text-[11px] text-neutral-500">
                    Méthode : <span className="font-mono">POST</span> · Content-Type : <span className="font-mono">application/json</span> ou <span className="font-mono">application/x-www-form-urlencoded</span>
                  </p>
                </div>
              )}

              {config?.configured && (
                <div className="mt-3 pt-3 border-t flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs"
                    onClick={disableWebhook}
                    disabled={saving}
                    data-testid="webhook-disable-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Désactiver le webhook
                  </Button>
                </div>
              )}
            </div>

            {/* Field mapping */}
            {config?.configured && (
              <div className="border rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-sm">Mapping des champs (optionnel)</h4>
                  <p className="text-[12px] text-neutral-500 mt-0.5">
                    AEGIS reconnaît automatiquement les noms standards (your-email, your-phone…). Si votre formulaire utilise des noms personnalisés, listez-les ici (séparés par des virgules, en priorité décroissante).
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {STD_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label className="text-[11px] uppercase tracking-wide text-neutral-500">{f.label}</Label>
                      <Input
                        value={fieldMap[f.key] || ''}
                        onChange={(e) => setFieldMap({ ...fieldMap, [f.key]: e.target.value })}
                        placeholder={`défaut : ${f.placeholder}`}
                        className="font-mono text-xs mt-1"
                        data-testid={`webhook-map-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-end">
                  <Button size="sm" onClick={saveFieldMap} disabled={saving} data-testid="webhook-save-map-btn">
                    Enregistrer le mapping
                  </Button>
                </div>
              </div>
            )}

            {/* Inspection panel — last 5 received submissions */}
            {config?.configured && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <Eye className="w-4 h-4 text-violet-600" /> Inspection — 5 dernières soumissions
                    </h4>
                    <p className="text-[12px] text-neutral-500 mt-0.5">
                      Permet à votre client / webmaster de tester le formulaire en direct et de voir exactement ce qu'AEGIS reçoit.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchRecent}
                    disabled={recentLoading}
                    data-testid="webhook-refresh-recent-btn"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${recentLoading ? 'animate-spin' : ''}`} />
                    Rafraîchir
                  </Button>
                </div>
                {recent.length === 0 ? (
                  <div className="text-center py-6 text-neutral-500 text-sm bg-neutral-50 rounded">
                    Aucune soumission reçue pour l'instant. Soumettez un test depuis votre formulaire — il apparaîtra ici en quelques secondes.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {recent.map((item, idx) => {
                      const isOpen = expandedIdx === idx;
                      const ts = item.received_at ? new Date(item.received_at) : null;
                      const tsStr = ts ? ts.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' }) : '';
                      const previewKeys = Object.keys(item.extracted || {}).slice(0, 3);
                      return (
                        <div key={idx} className="border rounded text-xs" data-testid={`webhook-recent-item-${idx}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedIdx(isOpen ? null : idx)}
                            className="w-full flex items-center justify-between p-2.5 hover:bg-neutral-50 text-left"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="font-mono text-[11px] text-neutral-500 shrink-0">{tsStr}</span>
                              <span className="text-neutral-700 truncate">
                                {previewKeys.length > 0
                                  ? previewKeys.map((k) => `${k}: ${item.extracted[k]}`).join(' · ')
                                  : <span className="text-amber-600">Aucun champ extrait — vérifiez le mapping ↑</span>}
                              </span>
                              {item.is_duplicate && (
                                <span className="px-1.5 py-0.5 rounded font-mono bg-orange-100 text-orange-700 text-[10px] shrink-0">
                                  Doublon
                                </span>
                              )}
                            </div>
                            <span className="text-neutral-400 ml-2">{isOpen ? '▾' : '▸'}</span>
                          </button>
                          {isOpen && (
                            <div className="border-t px-2.5 py-3 bg-neutral-50 space-y-3">
                              <div>
                                <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Champs extraits par AEGIS</div>
                                {Object.keys(item.extracted || {}).length > 0 ? (
                                  <div className="grid grid-cols-2 gap-1 font-mono text-[11px]">
                                    {Object.entries(item.extracted).map(([k, v]) => (
                                      <div key={k} className="truncate">
                                        <span className="text-neutral-500">{k}:</span> <span className="text-neutral-900">{v}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-amber-700 text-[12px]">
                                    Aucun champ standard reconnu. Listez ci-dessus les noms réels (ex. <code className="font-mono bg-amber-100 px-1">your-email</code>) dans la section <strong>Mapping</strong>.
                                  </div>
                                )}
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Payload brut reçu</div>
                                <pre className="bg-white border rounded p-2 text-[10px] font-mono overflow-x-auto max-h-40 leading-relaxed">
{JSON.stringify(item.raw_payload || {}, null, 2)}
                                </pre>
                              </div>
                              {item.lead_id && (
                                <div className="text-[11px] text-neutral-500">
                                  Lead créé : <code className="font-mono">{item.lead_id}</code>
                                  {item.duplicate_of && <> — doublon de <code className="font-mono">{item.duplicate_of}</code></>}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Help */}
            {config?.configured && (
              <div className="border border-blue-100 bg-blue-50/50 rounded-lg p-4 text-sm">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Comment installer côté formulaire
                </h4>
                <ul className="space-y-1.5 text-[13px] text-blue-900/90 list-disc list-inside">
                  <li><strong>Contact Form 7</strong> → plugin gratuit "<em>CF7 to Webhook</em>" → coller l'URL.</li>
                  <li><strong>WPForms</strong> → addon "Webhooks" (Pro) → URL + méthode POST + JSON.</li>
                  <li><strong>Gravity Forms</strong> → addon "Webhooks" → POST + JSON.</li>
                  <li><strong>Zapier / Make / n8n</strong> → action "Webhook POST" → URL.</li>
                </ul>
                <p className="text-[11px] text-blue-800/70 mt-2">
                  Procédure détaillée + exemples JSON : voir <span className="font-mono">/docs/webhook-wordpress.md</span>
                </p>
              </div>
            )}

            {/* Meta / LinkedIn Lead Ads — closed-loop par creative */}
            {config?.configured && (
              <div className="border border-violet-200 bg-violet-50/40 rounded-lg p-4 text-sm">
                <h4 className="font-medium text-violet-900 mb-1 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> ROI par creative — Meta &amp; LinkedIn Lead Ads
                </h4>
                <p className="text-[12px] text-violet-900/80 mb-3">
                  Ajoutez ces 4 champs au payload pour activer le tableau "Top creatives" sur la page <strong>Analyse leads SEA</strong> (savoir quel visuel publicitaire individuel rapporte vraiment du CA).
                </p>
                <div className="space-y-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-violet-800 font-medium mb-1">Meta — Zapier "Facebook Lead Ads"</div>
                    <pre className="bg-white border border-violet-100 rounded p-2 text-[11px] font-mono overflow-x-auto leading-relaxed">{`{
  "campaign_id": "{{trigger.campaign_id}}",
  "campaign_name": "{{trigger.campaign_name}}",
  "ad_id": "{{trigger.ad_id}}",
  "ad_name": "{{trigger.ad_name}}",
  "form_id": "{{trigger.form_id}}",
  "form_name": "{{trigger.form_name}}",
  "email": "{{trigger.email}}",
  "phone": "{{trigger.phone_number}}",
  "full_name": "{{trigger.full_name}}"
}`}</pre>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-violet-800 font-medium mb-1">LinkedIn — Zapier "LinkedIn Lead Gen Forms"</div>
                    <pre className="bg-white border border-violet-100 rounded p-2 text-[11px] font-mono overflow-x-auto leading-relaxed">{`{
  "_platform": "linkedin",
  "campaign_name": "{{trigger.campaign_name}}",
  "creative_name": "{{trigger.creative_name}}",
  "lead_form_name": "{{trigger.form_name}}",
  "email": "{{trigger.email}}",
  "full_name": "{{trigger.first_name}} {{trigger.last_name}}"
}`}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="webhook-close-btn">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
